'use client';

import { c as _c } from "react/compiler-runtime";
import { type ComponentPropsWithRef, type FC, useCallback } from 'react';
import { cn } from '../../utils/helpers.ts';
import { CarouselProvider, type CarouselStateOptions, useCarouselInternalContext, useCarouselState, useSlideInView } from './carousel.logic.ts';

/**
 * Requires exactly one of `aria-label` or `aria-labelledby`.

 * A carousel is an ARIA landmark (`role="region"`) and every slide is a named
 * group, so both must be labelled.
 */
type CarouselAccessibleName = {
  'aria-label': string;
  'aria-labelledby'?: never;
} | {
  'aria-label'?: never;
  'aria-labelledby': string;
};

/**
 * Props for the {@link Carousel} root.
 */
export type CarouselProps = Omit<ComponentPropsWithRef<'div'>, 'aria-label' | 'aria-labelledby'> & CarouselAccessibleName & CarouselStateOptions;

/**
 * Headless carousel root. Initializes the carousel, provides its state to its
 * descendants, and renders the labelled `region` landmark.
 */
export const Carousel: FC<CarouselProps> = t0 => {
  const $ = _c(32);
  let alignment;
  let children;
  let className;
  let dragAvailability;
  let dragMode;
  let initialSlide;
  let keyboardNavigation;
  let loop;
  let orientation;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      "aria-roledescription": t1,
      alignment,
      children,
      dragAvailability,
      dragMode,
      initialSlide,
      keyboardNavigation,
      loop,
      orientation,
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = alignment;
    $[2] = children;
    $[3] = className;
    $[4] = dragAvailability;
    $[5] = dragMode;
    $[6] = initialSlide;
    $[7] = keyboardNavigation;
    $[8] = loop;
    $[9] = orientation;
    $[10] = props;
    $[11] = ref;
    $[12] = t1;
  } else {
    alignment = $[1];
    children = $[2];
    className = $[3];
    dragAvailability = $[4];
    dragMode = $[5];
    initialSlide = $[6];
    keyboardNavigation = $[7];
    loop = $[8];
    orientation = $[9];
    props = $[10];
    ref = $[11];
    t1 = $[12];
  }
  const roleDescription = t1 === undefined ? "carousel" : t1;
  let t2;
  if ($[13] !== alignment || $[14] !== dragAvailability || $[15] !== dragMode || $[16] !== initialSlide || $[17] !== keyboardNavigation || $[18] !== loop || $[19] !== orientation) {
    t2 = {
      alignment,
      dragAvailability,
      dragMode,
      initialSlide,
      keyboardNavigation,
      loop,
      orientation
    };
    $[13] = alignment;
    $[14] = dragAvailability;
    $[15] = dragMode;
    $[16] = initialSlide;
    $[17] = keyboardNavigation;
    $[18] = loop;
    $[19] = orientation;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  const state = useCarouselState(t2);
  let t3;
  if ($[21] !== className) {
    t3 = cn("relative", className);
    $[21] = className;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  let t4;
  if ($[23] !== children || $[24] !== props || $[25] !== ref || $[26] !== roleDescription || $[27] !== t3) {
    t4 = <div {...props} ref={ref} className={t3} role="region" aria-roledescription={roleDescription}>{children}</div>;
    $[23] = children;
    $[24] = props;
    $[25] = ref;
    $[26] = roleDescription;
    $[27] = t3;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  let t5;
  if ($[29] !== state || $[30] !== t4) {
    t5 = <CarouselProvider value={state}>{t4}</CarouselProvider>;
    $[29] = state;
    $[30] = t4;
    $[31] = t5;
  } else {
    t5 = $[31];
  }
  return t5;
};

/**
 * The scroll window. This is the element Embla measures and scrolls, so its
 * ref must reach the Embla API. It clips overflow so only in-view slides show.
 *
 * Wrap a single {@link CarouselTrack} in it.
 */
export const CarouselViewport: FC<ComponentPropsWithRef<'div'>> = t0 => {
  const $ = _c(13);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  const {
    viewportRef
  } = useCarouselInternalContext();
  let t1;
  if ($[4] !== ref || $[5] !== viewportRef) {
    t1 = node => {
      viewportRef(node);
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          ref.current = node;
        }
      }
    };
    $[4] = ref;
    $[5] = viewportRef;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const composedViewportRef = t1;
  let t2;
  if ($[7] !== className) {
    t2 = cn("overflow-hidden", className);
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== composedViewportRef || $[10] !== props || $[11] !== t2) {
    t3 = <div {...props} ref={composedViewportRef} className={t2} />;
    $[9] = composedViewportRef;
    $[10] = props;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};

/**
 * The moving strip that holds the slides. Lays them out along the carousel's
 * axis (a flex row when horizontal, a flex column when vertical). Apply gap /
 * alignment utilities here.
 */
export const CarouselTrack: FC<ComponentPropsWithRef<'div'>> = t0 => {
  const $ = _c(11);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  const {
    orientation
  } = useCarouselInternalContext();
  const t1 = orientation === "horizontal" ? "flex-row" : "flex-col";
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = cn("flex", t1, className);
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== ref || $[9] !== t2) {
    t3 = <div {...props} ref={ref} className={t2} />;
    $[7] = props;
    $[8] = ref;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};

/**
 * Props for {@link CarouselSlide}: standard `<div>` props plus a required
 * accessible name and the slide's zero-based `index`.
 */
export type CarouselSlideProps = Omit<ComponentPropsWithRef<'div'>, 'aria-label' | 'aria-labelledby'> & CarouselAccessibleName & {
  /** Zero-based position of this slide, used to derive selected state. */
  index: number;
};

/**
 * A single slide. Rendered as a named `group` with
 * `aria-roledescription="slide"` so assistive tech announces it correctly.
 */
export const CarouselSlide: FC<CarouselSlideProps> = t0 => {
  const $ = _c(17);
  let className;
  let index;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      "aria-roledescription": t1,
      index,
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = index;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
  } else {
    className = $[1];
    index = $[2];
    props = $[3];
    ref = $[4];
    t1 = $[5];
  }
  const roleDescription = t1 === undefined ? "slide" : t1;
  const {
    inertHiddenSlides,
    orientation,
    selectedIndex
  } = useCarouselInternalContext();
  const inView = useSlideInView(index);
  const t2 = orientation === "horizontal" ? "min-w-0" : "min-h-0";
  let t3;
  if ($[6] !== className || $[7] !== t2) {
    t3 = cn(t2, className);
    $[6] = className;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = selectedIndex === index ? "" : undefined;
  const t5 = inView ? "" : undefined;
  const t6 = inertHiddenSlides && !inView ? true : undefined;
  let t7;
  if ($[9] !== props || $[10] !== ref || $[11] !== roleDescription || $[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <div {...props} ref={ref} className={t3} role="group" aria-roledescription={roleDescription} data-selected={t4} data-in-view={t5} inert={t6} />;
    $[9] = props;
    $[10] = ref;
    $[11] = roleDescription;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};

/**
 * Props for the previous/next control: standard `<button>` props with a
 * required direction and `aria-label` (the visible child is often just an
 * icon).
 */
export type CarouselControlProps = ComponentPropsWithRef<'button'> & {
  'aria-label': string;
  direction: 'next' | 'previous';
};

/**
 * Headless previous/next control. Renders a `<button>` that scrolls in the
 * selected direction and remains focusable at a scroll boundary while exposing
 * `aria-disabled` to assistive technology.
 *
 * Fully unstyled — this should usually be wrapped in consumer with <ButtonStyle /> or <LinkStyle />
 * */
export const CarouselControl: FC<CarouselControlProps> = t0 => {
  const $ = _c(17);
  let direction;
  let disabled;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      disabled,
      direction,
      type: t1,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = direction;
    $[2] = disabled;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
  } else {
    direction = $[1];
    disabled = $[2];
    props = $[3];
    ref = $[4];
    t1 = $[5];
  }
  const type = t1 === undefined ? "button" : t1;
  const {
    canScrollNext,
    canScrollPrevious,
    scrollNext,
    scrollPrevious
  } = useCarouselInternalContext();
  const canScroll = direction === "previous" ? canScrollPrevious : canScrollNext;
  const scroll = direction === "previous" ? scrollPrevious : scrollNext;
  const ariaDisabled = !canScroll || props["aria-disabled"] === true || props["aria-disabled"] === "true";
  const t2 = ariaDisabled || undefined;
  let t3;
  if ($[6] !== ariaDisabled || $[7] !== props || $[8] !== scroll) {
    t3 = event => {
      if (ariaDisabled) {
        event.preventDefault();
        return;
      }
      props.onClick?.(event);
      if (!event.defaultPrevented) {
        scroll();
      }
    };
    $[6] = ariaDisabled;
    $[7] = props;
    $[8] = scroll;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== disabled || $[11] !== props || $[12] !== ref || $[13] !== t2 || $[14] !== t3 || $[15] !== type) {
    t4 = <button {...props} ref={ref} type={type} disabled={disabled} aria-disabled={t2} onClick={t3} />;
    $[10] = disabled;
    $[11] = props;
    $[12] = ref;
    $[13] = t2;
    $[14] = t3;
    $[15] = type;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
};
export { type CarouselAlignment, type CarouselDragMode, type CarouselKeyboardNavigation, type CarouselOrientation, type CarouselState, useCarousel, useSlideInView } from './carousel.logic.ts';